import React from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";

const cases = [
  {
    name: "MetabolicLabOS",
    color: "#3aab5a",
    niche: "Healthcare",
    built: [
      "300+ SEO articles across 10 content hubs",
      "AI food logging and coaching (Kai)",
      "Pharmacokinetic dose timing charts",
      "Email nurture and lead magnets",
      "Stripe subscriptions ($19.99/$29.99/mo)",
    ],
    proves: "AuthorityOS works in health — the most competitive online niche.",
    revenue: "Subscriptions, digital guides, affiliates",
    status: "Live",
    action: { type: "link", label: "Visit MetabolicLabOS →", href: "https://metaboliclabos.com" },
  },
  {
    name: "BeaconsIEP",
    color: "#5a8aeb",
    niche: "Education",
    built: [
      "AI companion Grace trained on IEP advocacy",
      "22 digital guides (12 paid, 10 free)",
      "Bilingual English/Spanish full parity",
      "IEP Health Score acquisition engine",
      "Parent tracker and document vault",
    ],
    proves: "AuthorityOS works in education advocacy.",
    revenue: "Subscriptions ($19.99/$29.99), guide bundles",
    status: "Live",
    action: { type: "link", label: "Visit BeaconsIEP →", href: "https://beaconsiep.com" },
  },
  {
    name: "Build Ready",
    color: "#ba7a30",
    niche: "Finance",
    built: [
      "AI coach Atlas across 7 construction phases",
      "Educational content hub",
      "Loan eligibility and budget tools",
      "Phase-by-phase email nurture",
      "Digital product suite",
    ],
    proves: "AuthorityOS works in financial services.",
    revenue: "Lead gen, digital products, consulting",
    status: "In Development",
    action: { type: "badge", label: "Coming Soon" },
  },
  {
    name: "AASC",
    color: "#9a6adb",
    niche: "Compliance",
    built: [
      "AI governance certification framework",
      "Compliance content library",
      "Professional development resources",
      "Enterprise positioning",
    ],
    proves: "AuthorityOS positions an expert before a market matures.",
    revenue: "Certification fees, enterprise licensing",
    status: "Live",
    action: { type: "link", label: "Visit AASC →", href: "https://aasc.org" },
  },
  {
    name: "ChalkOS",
    color: "#eb5a5a",
    niche: "Sports",
    built: [
      "Full SaaS platform",
      "Messaging, scheduling, polls, weather",
      "3 subscription tiers",
      "League and multi-team management",
    ],
    proves: "AuthorityOS produces software products.",
    revenue: "Pickup (free) / Clipboard+ ($9/mo) / Chalkboard ($19/mo)",
    status: "Live",
    action: { type: "link", label: "Visit ChalkOS →", href: "https://chalkos.app" },
  },
  {
    name: "Kaiya Solutions",
    color: "#3a9acb",
    niche: "Enterprise",
    built: [
      "Enterprise platform / 11 modules",
      "300,000+ lines of code",
      "Governance and risk management systems",
      "Compliance infrastructure",
    ],
    proves: "AuthorityOS builds enterprise-scale systems.",
    revenue: "Enterprise licensing, professional services",
    status: "Live",
    action: { type: "link", label: "Visit Kaiya Solutions →", href: "https://kaiyasolutions.com" },
  },
];

const Section = ({ children, style }) => (
  <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "44px 32px", ...style }}>
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>{children}</div>
  </section>
);

export default function Portfolio() {
  return (
    <Layout title="The Labs | The Authority Gap" path="/portfolio">
      {/* HERO */}
      <section style={{ background: "#090909", padding: "70px 32px 48px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 880, margin: "0 auto" }}>
          <div style={{ marginBottom: 24 }}><EyebrowPill>The Proof</EyebrowPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 18 }}>
            <span>One company.</span><br />
            <span style={{ color: "#999" }}>Six case studies.</span>
          </h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>One framework proven six times across six different industries.</p>
        </div>
      </section>

      {/* REFRAME */}
      <Section>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ background: "#111", borderLeft: "3px solid #FF6575", borderRadius: "0 8px 8px 0", padding: 24 }}>
            <p style={{ color: "#bbb", fontSize: 13, fontStyle: "italic", lineHeight: 1.7, margin: 0 }}>
              These are not separate businesses. They are laboratories. They are proof. Each one was built using AuthorityOS to prove the framework works in a specific niche. When a prospective client asks "has this worked before?" — the answer is six live properties.
            </p>
          </div>
        </div>
      </Section>

      {/* CASE STUDIES */}
      <Section>
        {cases.map((c) => (
          <div
            key={c.name}
            data-testid={`case-${c.name.toLowerCase().replace(/\s/g, "")}`}
            style={{
              background: "#0f0f0f",
              border: "1px solid #1a1a1a",
              borderRadius: 10,
              padding: 24,
              marginBottom: 12,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, background: c.color }} />
            <div className="tag-case-grid" style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 24, paddingLeft: 16, alignItems: "center" }}>
              <div>
                <h3 style={{ color: c.color, fontSize: 20, fontWeight: 700, margin: "0 0 6px" }}>{c.name}</h3>
                <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>{c.niche}</div>
                <div style={{ height: 2, width: 40, background: "#FF6575", margin: "10px 0" }} />
                <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>What was built:</div>
                {c.built.map((b) => (
                  <div key={b} style={{ display: "flex", gap: 6, alignItems: "baseline", marginBottom: 4 }}>
                    <span style={{ width: 3, height: 3, background: "#FF6575", borderRadius: "50%", flexShrink: 0, marginTop: 5 }} />
                    <span style={{ color: "#bbb", fontSize: 11, fontWeight: 500 }}>{b}</span>
                  </div>
                ))}
                <p style={{ color: "#aaa", fontStyle: "italic", fontSize: 12, marginTop: 10, marginBottom: 0 }}>{c.proves}</p>
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ color: "#888", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4 }}>Revenue</div>
                <div style={{ color: "#bbb", fontSize: 11, marginBottom: 14, lineHeight: 1.5 }}>{c.revenue}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 14 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />
                  <span style={{ color: "#777", fontSize: 10, fontWeight: 600 }}>{c.status}</span>
                </div>
                {c.action.type === "link" ? (
                  <a
                    href={c.action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      background: "transparent",
                      border: `1px solid ${c.color}`,
                      color: c.color,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "8px 14px",
                      borderRadius: 6,
                    }}
                  >
                    {c.action.label}
                  </a>
                ) : (
                  <span style={{ display: "inline-block", background: c.color, color: "#fff", borderRadius: 20, padding: "4px 14px", fontSize: 11, fontWeight: 600 }}>
                    {c.action.label}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        <style>{`
          @media (max-width: 760px) {
            .tag-case-grid { grid-template-columns: 1fr !important; padding-left: 12px !important; }
          }
        `}</style>
      </Section>

      {/* STATS */}
      <Section style={{ textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 60, flexWrap: "wrap" }}>
          {[
            { n: "6", l: "Industries" },
            { n: "7", l: "Asset types deployed" },
            { n: "1", l: "Framework behind all of it" },
          ].map((s) => (
            <div key={s.l}>
              <div style={{ color: "#FF6575", fontSize: 32, fontWeight: 700 }}>{s.n}</div>
              <div style={{ color: "#888", fontSize: 10, textTransform: "uppercase", marginTop: 4, letterSpacing: "0.08em" }}>{s.l}</div>
            </div>
          ))}
        </div>
        <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.7, maxWidth: 520, margin: "20px auto 0" }}>
          Every property was built by one founder using one framework. If AuthorityOS can work in health, education, finance, compliance, sports, and enterprise — it can work in your niche.
        </p>
        <Link to="/score" data-testid="portfolio-cta-score" className="btn-primary" style={{ marginTop: 24 }}>
          Start with a free Authority Gap Score
        </Link>
      </Section>
    </Layout>
  );
}
